import { useMemo, useEffect } from 'react';
import { useRouter } from '~/utils/navigation';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { ButtonPrimary, Box, LabelBold } from '@comeon/ui';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectLocale } from '~/components/account/ducks/index.js';
import Icon, { ICON_WALLET_LINE, ICON_BOTTOM } from '~/components/icon/index.js';
import currencyFormatter from '../../../../libs/currency-formatter.js';
import Collapse from './collapse.js';
import useTranslations from '../../../translation/use-translations';
import useIntervalAction from '../../../../hooks/use-interval-action.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import useResources from '../../../resource/use-resources.js';
import { HeadWrapper, MainTitle, SubTitle } from '../../../misc/section-head.js';
import { fetchGameRoom, FETCH_GAME_ROOM, fetchPlayersBoughtIntoSession } from '../ducks/we-spin-duck.js';
import {
	selectStreamView,
	selectGameRoom,
	selectPlayersBoughtIntoSession,
	selectNextBuyInEventForSession,
	selectRunningEventForSession,
	selectConfiguration
} from '../ducks/we-spin-selectors.js';
import WeSpinGameRoomNextSessionItem from './we-spin-game-room-next-session-item.js';
import { BEFORE_RUNNING_STATES, RUNNING, BUY_IN_STATES, PAST_STATES } from '../constants/we-spin-stream-states.js';
import { WE_SPIN_INFO_ROOM_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import { RESOURCE_KEYS } from '../constants/we-spin-resource-keys.js';
import StartTime from '../components/start-time.js';
import { ROUTE_WE_SPIN_GAME_VIEW } from '../constants/we-spin-route-constants.js';
import { streamToGameRoomRoute } from '../utils/stream-to-game-room-route.js';

const BoxWithBackgroundColor = styled(Box)`
	background-color: rgba(var(--we-spin-gameroom-buyin-stream-bg-color, var(--base-12)), 1);
`;

const GameRoomBalance = ({ gameRoom, streamRef, translations: tr }) => {
	const locale = useSelector(selectLocale);
	const streamView = useSelector(selectStreamView(streamRef));

	const { currencyCode, noParticipants, state, isFreeWeSpin } = streamView;

	const { yourBuyIn, yourBetSize, yourShare, yourBalance, yourBoost } = gameRoom;
	// const { myRaffleTicketCount } = gameRoom; // Hide Raffles

	const hasStarted = !BUY_IN_STATES.includes(state);

	const dispatch = useDispatch();
	useEffect(() => {
		if (streamRef) {
			dispatch(fetchPlayersBoughtIntoSession(streamRef));
		}
	}, [dispatch, streamRef]);

	const playersBoughtIntoSession = useSelector(selectPlayersBoughtIntoSession(streamRef)) || null;

	const yourBetSizeFormatted = useMemo(
		() => currencyFormatter.format(yourBetSize, currencyCode, locale),
		[currencyCode, locale, yourBetSize]
	);

	const yourBuyInFormatted = useMemo(
		() => currencyFormatter.format(yourBuyIn, currencyCode, locale),
		[currencyCode, locale, yourBuyIn]
	);

	const yourNetResultFormatted = useMemo(
		() => (hasStarted ? currencyFormatter.format(yourBalance - yourBuyIn, currencyCode, locale) : ''),
		[currencyCode, hasStarted, locale, yourBalance, yourBuyIn]
	);

	const yourBalanceInFormatted = useMemo(
		() => (hasStarted ? currencyFormatter.format(yourBalance, currencyCode, locale) : ''),
		[currencyCode, hasStarted, locale, yourBalance]
	);

	const yourBoostFormatted = useMemo(
		() => (hasStarted ? currencyFormatter.format(yourBoost, currencyCode, locale) : ''),
		[currencyCode, hasStarted, locale, yourBoost]
	);

	const shareOfBalance = useMemo(
		() => (hasStarted ? `${(yourShare * 100).toFixed(2)}%` : ''),
		[hasStarted, yourShare]
	);

	if (isFreeWeSpin)
		return (
			<HeadWrapper>
				<Icon type={ICON_WALLET_LINE} />
				<MainTitle>{tr.WE_SPIN_INFO_ROOM_MAIN_TITLE_FREE_WESPIN}</MainTitle>
				<SubTitle>{tr.WE_SPIN_INFO_ROOM_MAIN_SUB_TITLE_FREE_WESPIN}</SubTitle>
			</HeadWrapper>
		);

	return (
		<>
			{BEFORE_RUNNING_STATES.includes(state) && (
				<HeadWrapper>
					<Icon type={ICON_WALLET_LINE} />
					<MainTitle>{tr.WE_SPIN_INFO_ROOM_MAIN_TITLE_BEFORE_RUNNING}</MainTitle>
					<SubTitle>{tr.WE_SPIN_INFO_ROOM_MAIN_SUB_TITLE_BEFORE_RUNNING}</SubTitle>
				</HeadWrapper>
			)}
			{hasStarted && (
				<div className="u-pl1 u-pr1 u-pb1">
					<Collapse
						label={tr.WE_SPIN_INFO_ROOM_CURRENT_RESULT_LABEL}
						text={tr.WE_SPIN_INFO_ROOM_CURRENT_RESULT_TEXT}
						dataAt="collapse-button-game-room-info-current-result"
						value={yourNetResultFormatted}
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					<Collapse
						label={tr.WE_SPIN_INFO_ROOM_CURRENT_BALANCE_LABEL}
						text={tr.WE_SPIN_INFO_ROOM_CURRENT_BALANCE_TEXT}
						dataAt="collapse-button-game-room-info-current-balance"
						value={yourBalanceInFormatted}
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					<Collapse
						label={tr.WE_SPIN_INFO_ROOM_MY_BUY_IN_LABEL}
						text={tr.WE_SPIN_INFO_ROOM_MY_BUY_IN_TEXT}
						dataAt="collapse-button-game-room-info-my-buyin"
						value={yourBuyInFormatted}
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					<Collapse
						label={tr.WE_SPIN_INFO_ROOM_MY_BOOST_AMOUNT_LABEL}
						text={tr.WE_SPIN_INFO_ROOM_MY_BOOST_AMOUNT_TEXT}
						dataAt="collapse-button-game-room-info-my-boost"
						value={yourBoostFormatted}
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					<Collapse
						label={tr.WE_SPIN_INFO_ROOM_MY_BET_RANGE_LABEL}
						text={tr.WE_SPIN_INFO_ROOM_MY_BET_RANGE_TEXT}
						dataAt="collapse-button-game-room-info-my-bet-range"
						value={yourBetSizeFormatted}
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					<Collapse
						label={tr.WE_SPIN_INFO_ROOM_BALANCE_SHARE_LABEL}
						text={tr.WE_SPIN_INFO_ROOM_BALANCE_SHARE_TEXT}
						dataAt="collapse-button-game-room-info-my-balance-share"
						value={shareOfBalance}
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					<Collapse
						label={tr.WE_SPIN_INFO_ROOM_PLAYERS_LABEL}
						text={
							(playersBoughtIntoSession && playersBoughtIntoSession.join(', ')) ||
							tr.WE_SPIN_INFO_ROOM_PLAYERS_TEXT
						}
						dataAt="collapse-button-game-room-info-players"
						value={noParticipants}
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					{/* {myRaffleTicketCount > 0 && (
						<Collapse
							label={tr.WE_SPIN_YOUR_RAFFLE_LABEL}
							text={tr.WE_SPIN_YOUR_RAFFLE_TICKET_INFO}
							dataAt="collapse-button-game-room-info-raffle-tickets"
							value={myRaffleTicketCount}
							infoIcon
							iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
						/>
					)} // Hide Raffles */}
				</div>
			)}
		</>
	);
};

const WeSpinGameRoomInfo = ({ streamView, buttonRef }) => {
	const router = useRouter();
	const { weSpinViewLiveStreamDataPollInterval } = useSelector(selectConfiguration);

	const { resources, isLoading: isLoadingResources } = useResources(RESOURCE_KEYS);

	const { streamRef: sessionRef, isFreeWeSpin } = streamView;

	const runningStreamView = useSelector(selectRunningEventForSession(sessionRef)) || streamView;

	const { streamRef: currentStreamRef } = runningStreamView;

	const nextBuyInStreamView = useSelector(selectNextBuyInEventForSession(sessionRef)) || runningStreamView;
	const locale = useSelector(selectLocale);
	const authenticated = useSelector(authenticationSelector);

	const isNextEventDisabled = nextBuyInStreamView?.isEventDisabled;
	const isNextEventFull = nextBuyInStreamView?.playerCapMet;
	const isNextEventFreeWeSpin = nextBuyInStreamView?.isFreeWeSpin;

	const infoStreamRef = runningStreamView.state !== RUNNING ? nextBuyInStreamView.streamRef : currentStreamRef;
	useIntervalAction(
		`${FETCH_GAME_ROOM}/${infoStreamRef}`,
		fetchGameRoom(infoStreamRef),
		weSpinViewLiveStreamDataPollInterval
	);
	const { translations, isLoadingTranslations } = useTranslations(WE_SPIN_INFO_ROOM_TEXT_KEYS);
	const transformPath = useContextualPathTransformer();
	const gameRoom = useSelector(selectGameRoom(infoStreamRef));
	if (isLoadingTranslations || !gameRoom || isLoadingResources) return null;

	const isParticipator = gameRoom.yourBuyIn > 0;

	const onClickLink = sv => {
		const link = transformPath(
			sv.state === RUNNING ? `${ROUTE_WE_SPIN_GAME_VIEW}/${currentStreamRef}` : streamToGameRoomRoute(sv)
		);
		router.push(link);
	};

	return (
		<>
			{isParticipator && !PAST_STATES.includes(gameRoom.state) ? (
				<GameRoomBalance gameRoom={gameRoom} streamRef={infoStreamRef} translations={translations} />
			) : isFreeWeSpin && authenticated ? (
				<HeadWrapper>
					<Icon type={ICON_WALLET_LINE} />
					<MainTitle>{translations.WE_SPIN_INFO_ROOM_MAIN_TITLE_FREE_WESPIN}</MainTitle>
					<SubTitle>{translations.WE_SPIN_INFO_ROOM_MAIN_SUB_TITLE_FREE_WESPIN}</SubTitle>
				</HeadWrapper>
			) : isFreeWeSpin && !authenticated ? (
				<HeadWrapper>
					<Icon type={ICON_WALLET_LINE} />
					<MainTitle>{translations.WE_SPIN_INFO_ROOM_NOT_OPEN_MAIN_TITLE_FREE_WESPIN}</MainTitle>
					<SubTitle>{translations.WE_SPIN_INFO_ROOM_NOT_OPEN_MAIN_SUB_TITLE_FREE_WESPIN}</SubTitle>
				</HeadWrapper>
			) : (
				<HeadWrapper>
					<Icon type={ICON_WALLET_LINE} />
					<MainTitle>{translations.WE_SPIN_BALANCE_INFO_NOT_OPEN_TITLE}</MainTitle>
					<SubTitle>{translations.WE_SPIN_BALANCE_INFO_NOT_OPEN_TEXT}</SubTitle>
				</HeadWrapper>
			)}
			{nextBuyInStreamView &&
				BUY_IN_STATES.includes(nextBuyInStreamView.state) &&
				buttonRef.current &&
				ReactDOM.createPortal(
					<BoxWithBackgroundColor flex="1" mb="0" p="5">
						<section className="u-text-c">
							<div className="u-mb1">
								<LabelBold>
									<StartTime state={nextBuyInStreamView.state} start={nextBuyInStreamView.start} />
								</LabelBold>
							</div>
						</section>
						<section>
							<Box my="3">
								<WeSpinGameRoomNextSessionItem
									key={nextBuyInStreamView.streamRef}
									locale={locale}
									stream={nextBuyInStreamView}
									translations={translations}
									showGameIcon
									showStartTime={false}
									onClick={e => {
										e.preventDefault();
										e.stopPropagation();
										onClickLink(nextBuyInStreamView);
									}}
									resources={resources}
								/>
							</Box>
						</section>
						{!nextBuyInStreamView.joined && (
							<ButtonPrimary
								disabled={isNextEventDisabled || isNextEventFull}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									onClickLink(nextBuyInStreamView);
								}}
							>
								{!isNextEventFreeWeSpin ? 'Buy-In' : translations.WE_SPIN_NEXT_SESSION_JOIN_FOR_FREE}
							</ButtonPrimary>
						)}
					</BoxWithBackgroundColor>,
					buttonRef.current
				)}
		</>
	);
};

export default WeSpinGameRoomInfo;
